/**
 * Map Store - manages the current map document and editing operations
 */

import { defineStore } from 'pinia';
import { ref, computed, shallowRef } from 'vue';
import {
    MapDocument,
    TileData,
    createEmptyMap,
    TERRAIN_PRESETS,
} from '@/formats/types';
import { parseAmd, loadAmdFile, downloadAmd } from '@/formats/amd';
import { useAssetsStore } from '@/stores/assets';

/** Undo/redo operation */
interface MapOperation {
    /** Tiles before the operation */
    before: Map<string, TileData>;
    /** Tiles after the operation */
    after: Map<string, TileData>;
}

/** Maximum undo history size */
const MAX_UNDO_HISTORY = 50;

export const useMapStore = defineStore('map', () => {
    // State
    const document = shallowRef<MapDocument | null>(null);
    const undoStack = ref<MapOperation[]>([]);
    const redoStack = ref<MapOperation[]>([]);
    const currentOperation = ref<MapOperation | null>(null);

    // Computed
    const hasMap = computed(() => document.value !== null);
    const isDirty = computed(() => document.value?.dirty ?? false);
    const canUndo = computed(() => undoStack.value.length > 0);
    const canRedo = computed(() => redoStack.value.length > 0);
    const mapWidth = computed(() => document.value?.width ?? 0);
    const mapHeight = computed(() => document.value?.height ?? 0);
    const filename = computed(() => document.value?.filename ?? 'untitled.amd');

    // Actions

    /**
     * Get flat array of tiles for sprite loading
     */
    function getFlatTiles(map: MapDocument): Array<{ tileSprite: number; objectSprite: number }> {
        const flat: Array<{ tileSprite: number; objectSprite: number }> = [];
        for (let x = 0; x < map.width; x++) {
            for (let y = 0; y < map.height; y++) {
                flat.push({
                    tileSprite: map.tiles[x][y].tileSprite,
                    objectSprite: map.tiles[x][y].objectSprite,
                });
            }
        }
        return flat;
    }

    /**
     * Create a new empty map
     */
    async function newMap(width: number, height: number, terrainIndex: number = 0): Promise<void> {
        const assetsStore = useAssetsStore();
        const map = createEmptyMap(width, height);
        const preset = TERRAIN_PRESETS[terrainIndex] ?? TERRAIN_PRESETS[0];

        // Fill with terrain preset
        for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
                map.tiles[x][y].tileSprite = preset.tileSprite;
                map.tiles[x][y].tileSpriteFrame = preset.tileFrame;
            }
        }

        document.value = map;
        undoStack.value = [];
        redoStack.value = [];

        // Load required sprites
        await assetsStore.loadSpritesForMap(getFlatTiles(map));
    }

    /**
     * Load a map from a File
     */
    async function loadMap(file: File): Promise<void> {
        const assetsStore = useAssetsStore();
        const map = await loadAmdFile(file);
        document.value = map;
        undoStack.value = [];
        redoStack.value = [];

        // Load required sprites
        await assetsStore.loadSpritesForMap(getFlatTiles(map));
    }

    /**
     * Load a map from an ArrayBuffer
     */
    async function loadMapFromBuffer(buffer: ArrayBuffer, filename?: string): Promise<void> {
        const assetsStore = useAssetsStore();
        const map = parseAmd(buffer, filename);
        document.value = map;
        undoStack.value = [];
        redoStack.value = [];

        // Load required sprites
        await assetsStore.loadSpritesForMap(getFlatTiles(map));
    }

    /**
     * Save the current map (download)
     */
    function saveMap(newFilename?: string): void {
        if (!document.value) return;

        downloadAmd(document.value, newFilename);
        document.value = { ...document.value, dirty: false };
    }

    /**
     * Get tile at coordinates
     */
    function getTile(x: number, y: number): TileData | null {
        if (!document.value) return null;
        if (x < 0 || x >= document.value.width) return null;
        if (y < 0 || y >= document.value.height) return null;
        return document.value.tiles[x][y];
    }

    /**
     * Begin a new editing operation (for undo grouping)
     */
    function beginOperation(): void {
        currentOperation.value = {
            before: new Map(),
            after: new Map(),
        };
    }

    /**
     * End the current operation and push to undo stack
     */
    function endOperation(): void {
        if (!currentOperation.value) return;
        if (currentOperation.value.before.size === 0) {
            currentOperation.value = null;
            return;
        }

        undoStack.value.push(currentOperation.value);
        if (undoStack.value.length > MAX_UNDO_HISTORY) {
            undoStack.value.shift();
        }
        redoStack.value = [];
        currentOperation.value = null;

        if (document.value) {
            document.value = { ...document.value, dirty: true };
        }
    }

    /**
     * Set tile at coordinates
     */
    function setTile(x: number, y: number, tile: Partial<TileData>): void {
        if (!document.value) return;
        if (x < 0 || x >= document.value.width) return;
        if (y < 0 || y >= document.value.height) return;

        const key = `${x},${y}`;
        const currentTile = document.value.tiles[x][y];

        // Record for undo if we have an active operation
        if (currentOperation.value && !currentOperation.value.before.has(key)) {
            currentOperation.value.before.set(key, { ...currentTile });
        }

        // Apply changes
        const newTiles = [...document.value.tiles];
        newTiles[x] = [...newTiles[x]];
        newTiles[x][y] = { ...currentTile, ...tile };

        // Record after state
        if (currentOperation.value) {
            currentOperation.value.after.set(key, { ...newTiles[x][y] });
        }

        document.value = { ...document.value, tiles: newTiles };
    }

    /**
     * Set base tile sprite
     */
    function setBaseTile(
        x: number,
        y: number,
        tileSprite: number,
        tileSpriteFrame: number
    ): void {
        setTile(x, y, { tileSprite, tileSpriteFrame });
    }

    /**
     * Set object tile
     */
    function setObject(
        x: number,
        y: number,
        objectSprite: number,
        objectSpriteFrame: number
    ): void {
        setTile(x, y, { objectSprite, objectSpriteFrame });
    }

    /**
     * Clear object at position
     */
    function clearObject(x: number, y: number): void {
        setTile(x, y, { objectSprite: 0, objectSpriteFrame: 0 });
    }

    /**
     * Set tile flags
     */
    function setTileFlags(
        x: number,
        y: number,
        flags: { blocked?: boolean; teleport?: boolean; farming?: boolean; buildable?: boolean }
    ): void {
        setTile(x, y, flags);
    }

    /**
     * Undo last operation
     */
    function undo(): void {
        if (!document.value || undoStack.value.length === 0) return;

        const operation = undoStack.value.pop()!;
        redoStack.value.push(operation);

        // Apply before state
        const newTiles = [...document.value.tiles];
        for (const [key, tile] of operation.before) {
            const [x, y] = key.split(',').map(Number);
            newTiles[x] = [...newTiles[x]];
            newTiles[x][y] = tile;
        }

        document.value = { ...document.value, tiles: newTiles };
    }

    /**
     * Redo last undone operation
     */
    function redo(): void {
        if (!document.value || redoStack.value.length === 0) return;

        const operation = redoStack.value.pop()!;
        undoStack.value.push(operation);

        // Apply after state
        const newTiles = [...document.value.tiles];
        for (const [key, tile] of operation.after) {
            const [x, y] = key.split(',').map(Number);
            newTiles[x] = [...newTiles[x]];
            newTiles[x][y] = tile;
        }

        document.value = { ...document.value, tiles: newTiles };
    }

    return {
        // State
        document,
        // Computed
        hasMap,
        isDirty,
        canUndo,
        canRedo,
        mapWidth,
        mapHeight,
        filename,
        // Actions
        newMap,
        loadMap,
        loadMapFromBuffer,
        saveMap,
        getTile,
        beginOperation,
        endOperation,
        setTile,
        setBaseTile,
        setObject,
        clearObject,
        setTileFlags,
        undo,
        redo,
    };
});
